import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { toast } from "sonner";

import {
  BaseForm,
  FormInput,
  FormMultiSelect,
  FormToggle,
} from "components/BaseForm";
import { Spinner } from "@/components/ui/spinner";
import revOppsConstants from "constants/RevenueOpportunity";
import { upperFirst } from "helpers/string";
import RecipientService from "services/Recipient";

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  types: z.array(z.string()).default([]),
  enabled: z.boolean(),
});

function RecipientForm({ model, onSuccess }) {
  const { data: recipient } = useQuery({
    queryKey: ["recipient", model?.id],
    queryFn: () => RecipientService.get(model.id),
    enabled: !!model?.id,
  });

  const initialValues = useMemo(() => {
    if (!model?.id) {
      return { name: "", types: [], enabled: true };
    }
    return (
      recipient && {
        name: recipient.name,
        types: recipient.types ?? [],
        enabled: recipient.enabled,
      }
    );
  }, [model?.id, recipient]);

  const recipientTypeOptions = useMemo(
    () =>
      Object.values(revOppsConstants.recipientType).map(option => ({
        key: option,
        text: upperFirst(option),
        value: option,
      })),
    []
  );

  const handleSubmit = async values => {
    const data = {
      name: values.name,
      types: values.types,
      enabled: values.enabled,
    };
    if (model?.id) {
      await RecipientService.update(model.id, data);
    } else {
      await RecipientService.create(data);
    }
    toast.success("Recipient saved");
  };

  if (!initialValues) {
    return <Spinner />;
  }

  return (
    <BaseForm
      schema={schema}
      defaultValues={initialValues}
      onSubmit={handleSubmit}
      onSuccess={onSuccess}
    >
      <FormInput name="name" label="Name" required />
      <FormMultiSelect
        name="types"
        label="Choose Recipient Type"
        required
        options={recipientTypeOptions}
      />
      <FormToggle name="enabled" label="Enabled" />
    </BaseForm>
  );
}

export default RecipientForm;
